/* eslint-disable react/react-in-jsx-scope */
import { BaseQuestion } from '@/components_v2/Formularies/BaseForm';
import RenderQuestions from '@/components_v2/Questions';
import {
  QuestionModel,
  Section,
} from '@/modules/core/domain/interfaces/IFormulary';
import { IReducedAnswerState } from '@/modules/SurveyForm';
import { DownloadIcon } from '@chakra-ui/icons';
import { Box, Button, HStack } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FavoriteButton from './components/FavoriteButton';
import FavoritesList from './components/FavoritesList';
import { orderSection } from './data';
import { useExporterFavorites } from './hooks/useExporterFavorites';
import { CreateMainProps } from './interfaces';

const renderQuestion = (question: QuestionModel) => {
  return <BaseQuestion question={question} render={RenderQuestions} />;
};

const renderSection = (section: Section) => {
  const orderedSection = orderSection(section);
  const questions = orderedSection.questions
    .filter((q) => q.enabled)
    .map(renderQuestion);
  return questions;
};

const ExporterFormRender: React.FC<CreateMainProps> = ({
  sections,
  submitForm,
  buttonLabel,
  screenName,
  getCurrentValues,
  onApplyFavorite,
  valuesVersion,
}) => {
  const { t } = useTranslation();

  const {
    favorites,
    addFavorite,
    removeFavorite,
    findMatchingFavorite,
  } = useExporterFavorites({ screenName: screenName || '' });

  const [activeFavoriteId, setActiveFavoriteId] = useState<string | null>(null);
  const lastValuesJsonRef = useRef<string>('');

  // Check for matching favorite when valuesVersion changes
  useEffect(() => {
    if (!screenName) return;
    
    // Small delay to ensure form state is updated
    const timer = setTimeout(() => {
      const currentValues = getCurrentValues();
      const match = findMatchingFavorite(currentValues);
      setActiveFavoriteId(match?.id || null);
    }, 50);

    return () => clearTimeout(timer);
  }, [valuesVersion, screenName, getCurrentValues, findMatchingFavorite, favorites]);

  // Poll for form changes to update favorite status
  useEffect(() => {
    if (!screenName || favorites.length === 0) return;

    const checkForChanges = () => {
      const currentValues = getCurrentValues();
      const currentJson = JSON.stringify(currentValues);
      
      // Only update if values actually changed
      if (currentJson !== lastValuesJsonRef.current) {
        lastValuesJsonRef.current = currentJson;
        const match = findMatchingFavorite(currentValues);
        setActiveFavoriteId(match?.id || null);
      }
    };

    // Check every 500ms for changes
    const interval = setInterval(checkForChanges, 500);

    return () => clearInterval(interval);
  }, [screenName, favorites, getCurrentValues, findMatchingFavorite]);

  const isFavorited = !!activeFavoriteId;

  const handleSaveFavorite = useCallback(
    (name: string, values: IReducedAnswerState) => {
      const newFavorite = addFavorite(name, values);
      setActiveFavoriteId(newFavorite.id);
    },
    [addFavorite],
  );

  const handleRemoveFavorite = useCallback(() => {
    if (activeFavoriteId) {
      removeFavorite(activeFavoriteId);
      setActiveFavoriteId(null);
    }
  }, [activeFavoriteId, removeFavorite]);

  const handleApplyFavorite = useCallback(
    (values: IReducedAnswerState) => {
      onApplyFavorite(values);
    },
    [onApplyFavorite],
  );

  const handleDeleteFavorite = useCallback(
    (id: string) => {
      removeFavorite(id);
      if (activeFavoriteId === id) {
        setActiveFavoriteId(null);
      }
    },
    [removeFavorite, activeFavoriteId],
  );

  const handleCheckFavorite = useCallback((): boolean => {
    const currentValues = getCurrentValues();
    const match = findMatchingFavorite(currentValues);
    setActiveFavoriteId(match?.id || null);
    return !!match;
  }, [getCurrentValues, findMatchingFavorite]);

  return (
    <>
      {screenName && favorites.length > 0 && (
        <FavoritesList
          favorites={favorites}
          activeFavoriteId={activeFavoriteId}
          onApplyFavorite={handleApplyFavorite}
          onDeleteFavorite={handleDeleteFavorite}
        />
      )}
      {sections.map(renderSection)}
      <Box position="sticky" bottom={-2} bg="white" py="4">
        <HStack spacing={2}>
          <Button
            flex={1}
            colorScheme="primary"
            size="md"
            leftIcon={<DownloadIcon />}
            onClick={submitForm}
          >
            {`${t('common.export')} ${buttonLabel || t('common.table')}`}
          </Button>
          {screenName && (
            <FavoriteButton
              isFavorited={isFavorited}
              onSaveFavorite={handleSaveFavorite}
              onRemoveFavorite={handleRemoveFavorite}
              getCurrentValues={getCurrentValues}
              onCheckFavorite={handleCheckFavorite}
            />
          )}
        </HStack>
      </Box>
    </>
  );
};

export default ExporterFormRender;
